import { useScreenSize } from '@hooks/useScreenSize';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { useUserProductsAndTenorsContext } from '@shared/contexts/UserProductsAndTenorsProvider';
import { Product, productMaps, ProductTenor } from '@shared/protos/product';
import { PermissionsEnum } from '@shared/protos/user';
import { ColumnMovedEvent, ColumnResizedEvent } from 'ag-grid-enterprise';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GridRef } from '../grid';
import { DefaultGridState, GridHandlers, GridSettings, RollingRowSettings } from '../shared/types';
import { getProducts, getTenors, getTenorsFirstIndexes, getTenorsFromRollingSettings } from '../shared/utils';
import { BidAskType, PricesRow } from './types';
import { mapColumns, mapRows } from './utils';

export type PricingGridState = DefaultGridState & {
  selectedBidAskColumns: BidAskType[];
  isShowCurrencyUnit: boolean;
  columnWidths: Record<string, number>;
};
export type PricingGrid = GridSettings<PricesRow> &
  GridHandlers<PricesRow> & {
    productMap: Record<string, Product>;
    tenorMap: Record<string, ProductTenor>;
    setNewBidAskColumns: (newBidAskColumns: BidAskType[]) => void;
    selectedBidAskColumns: BidAskType[];
    isShowCurrencyUnit: boolean;
    toggleIsShowCurrencyUnit: () => void;
    columnWidths: Record<string, number>;
  };

export function usePricingGridState(
  selectedColumns: string[],
  selectedRows: string[],
  columnsOrder: string[],
  userRollingRowSettings: RollingRowSettings,
  isOverrideRolling: boolean,
  selectedBidAskColumns: BidAskType[],
  isShowCurrencyUnit: boolean,
  columnWidths: Record<string, number>
): PricingGrid {
  const gridRef = useRef<GridRef<PricesRow>>(null);
  const user = useUserContext();
  const { userProducts, tenors } = useUserProductsAndTenorsContext();
  const { isMobile } = useScreenSize();
  const isBroker = useMemo(() => !!user?.permissions?.includes(PermissionsEnum.BROKER), [user?.permissions]);

  const [state, setState] = useState<PricingGridState>({
    selectedColIds: selectedColumns,
    selectedRowIds: selectedRows,
    rollingRowSettings: userRollingRowSettings,
    columnsOrder: columnsOrder,
    isOverrideRolling,
    selectedBidAskColumns: selectedBidAskColumns,
    isShowCurrencyUnit: isShowCurrencyUnit,
    columnWidths: columnWidths,
  });

  const selectedUserProducts = useMemo(() => getProducts(userProducts, state.selectedColIds), [userProducts, state.selectedColIds]);
  const viewableProductColumns = useMemo(
    () =>
      selectedUserProducts.filter(
        product =>
          !product.underlying_symbol ||
          (product.calendar_type === 'spread' && product.tenor_frequency !== 'quarterly') ||
          product.product_type === 'moc'
      ),
    [selectedUserProducts]
  );
  const selectedTenors = useMemo(() => getTenors(tenors, state.selectedRowIds), [tenors, state.selectedRowIds]);
  const { firstWeekIndex, firstMonthIndex } = getTenorsFirstIndexes(selectedTenors, tenors);
  const { productMap, tenorMap } = useMemo(() => productMaps(selectedUserProducts, selectedTenors), [selectedUserProducts, selectedTenors]);

  const toggleColumn = useCallback(
    (id: string) =>
      setState(prevState => ({
        ...prevState,
        selectedColIds: prevState.selectedColIds.includes(id)
          ? prevState.selectedColIds.filter(colId => colId !== id)
          : [...prevState.selectedColIds, id],
      })),
    []
  );

  const setNewBidAskColumns = useCallback(
    (newBidAskColumns: BidAskType[]) =>
      setState(prevState => ({
        ...prevState,
        selectedBidAskColumns: newBidAskColumns,
      })),
    []
  );

  const addColumn = useCallback(
    (id: string) =>
      setState(prevState => ({
        ...prevState,
        selectedColIds: prevState.selectedColIds.includes(id) ? prevState.selectedColIds : [...prevState.selectedColIds, id],
      })),
    []
  );

  const toggleRow = useCallback((id: string) => {
    setState(prevState => ({
      ...prevState,
      selectedRowIds: prevState.selectedRowIds.includes(id)
        ? prevState.selectedRowIds.filter(rowId => rowId !== id)
        : [...prevState.selectedRowIds, id],
    }));
  }, []);

  const addRow = useCallback((id: string) => {
    setState(prevState => ({
      ...prevState,
      selectedRowIds: prevState.selectedRowIds.includes(id) ? prevState.selectedRowIds : [...prevState.selectedRowIds, id],
    }));
  }, []);

  const updateRollingSettings = useCallback(
    (newSettings: RollingRowSettings) => {
      const newSelectedRowIds = getTenorsFromRollingSettings(tenors, newSettings).map(elem => elem.code);

      setState(prevState => ({
        ...prevState,
        rollingRowSettings: newSettings,
        selectedRowIds: newSelectedRowIds,
      }));
    },
    [tenors]
  );

  const onColumnMoved = useCallback(
    ({ api, finished }: ColumnMovedEvent<PricesRow>) => {
      if (!finished) return;
      const columnsOrder = api.getColumnState().map(({ colId }) => colId);
      setState(prevState => ({
        ...prevState,
        columnsOrder,
      }));
    },
    [state.columnsOrder]
  );

  const onColumnResized = useCallback((params: ColumnResizedEvent) => {
    const api = params.api;
    if (!api || !params.finished || params.source === 'gridOptionsChanged') return;
    const newColumnWidths = api.getColumnState().reduce((acc, col) => {
      if (col.colId) {
        acc[col.colId] = col.width || 0;
      }
      return acc;
    }, {} as Record<string, number>);
    setState(prevState => ({
      ...prevState,
      columnWidths: newColumnWidths,
    }));
  }, []);

  const toggleIsOverrideRolling = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      isOverrideRolling: !prevState.isOverrideRolling,
    }));
  }, []);

  const toggleIsShowCurrencyUnit = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      isShowCurrencyUnit: !prevState.isShowCurrencyUnit,
    }));
  }, []);

  // GET ROW IDS FROM ROLLING SETTINGS IF NOT OVERRIDE ROLLING
  useEffect(() => {
    const { rollingRowSettings, isOverrideRolling } = state;

    if (!isOverrideRolling) {
      const newSelectedRowIds = getTenorsFromRollingSettings(tenors, rollingRowSettings).map(elem => elem.code);

      setState(prevState => ({
        ...prevState,
        selectedRowIds: newSelectedRowIds,
      }));
    }
  }, [tenors]);

  return useMemo(
    () => ({
      ...state,
      columnDefs: {
        lockPinned: true,
        valueFormatter: params => {
          if (params.value === '0') return '0.00';
          if (params.value == null) return '';
          return params.value;
        },
      },
      gridRef,
      rows: mapRows(
        selectedTenors,
        selectedUserProducts.filter(({ symbol }) => state.selectedColIds.includes(symbol)),
        firstWeekIndex,
        firstMonthIndex
      ),
      columns: mapColumns(
        viewableProductColumns,
        state.selectedColIds,
        state.columnsOrder,
        isMobile,
        state.selectedBidAskColumns,
        state.isShowCurrencyUnit,
        state.columnWidths,
        isBroker
      ),
      onColumnMoved,
      onColumnResized,
      toggleColumn,
      setNewBidAskColumns,
      addColumn,
      toggleRow,
      addRow,
      updateRollingSettings,
      toggleIsOverrideRolling,
      filteredSelectedTenors: selectedTenors,
      selectedUserProducts,
      productMap,
      tenorMap,
      toggleIsShowCurrencyUnit,
    }),
    [
      selectedTenors,
      selectedUserProducts,
      firstWeekIndex,
      firstMonthIndex,
      state,
      viewableProductColumns,
      productMap,
      tenorMap,
      isMobile,
      isBroker,
      state.columnWidths,
    ]
  );
}
